import { useEffect, useRef } from "react";

/**
 * useLiveRefresh(fn, { intervalMs, enabled })
 *  - Calls fn() on mount, on window focus, on 'online' event,
 *    and every intervalMs while the tab is visible.
 */
export function useLiveRefresh(fn, { intervalMs = 15000, enabled = true } = {}) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled) return undefined;
    let timer = null;

    const tick = () => {
      if (document.visibilityState === "visible") fnRef.current?.();
    };

    const start = () => {
      stop();
      tick();
      timer = setInterval(tick, intervalMs);
    };
    const stop = () => {
      if (timer) clearInterval(timer);
      timer = null;
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") start();
      else stop();
    };

    start();
    window.addEventListener("focus", tick);
    window.addEventListener("online", tick);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stop();
      window.removeEventListener("focus", tick);
      window.removeEventListener("online", tick);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, intervalMs]);
}
